import { useEffect, useRef } from "react";
import { Logo } from "@/components/Logo";

/**
 * OAuth Bridge page.
 *
 * Emergent's managed Google OAuth only allows redirects to *.emergent.host
 * and *.emergentagent.com. To support custom domains, we route OAuth through
 * this bridge page (hosted on the *.emergent.host URL), which then forwards
 * the session_id fragment to the original custom-domain origin.
 *
 * Expected URL shape:
 *   https://<app>.emergent.host/auth/oauth-bridge?return_to=<encoded-target>#session_id=...
 *
 * For safety we only redirect to whitelisted origins.
 */
const ALLOWED_RETURN_HOSTS = [
  /^https:\/\/[a-z0-9-]+\.emergent\.host$/i,
  /^https:\/\/[a-z0-9-]+\.preview\.emergentagent\.com$/i,
  /^https:\/\/[a-z0-9-]+\.emergentagent\.com$/i,
  /^https:\/\/notewise\.businessbreakthroughhub\.com$/i,
  /^https:\/\/noteology\.businessbreakthroughhub\.com$/i,
  /^http:\/\/localhost(:\d+)?$/i,
];

function isAllowedReturnUrl(url) {
  try {
    const u = new URL(url);
    const origin = u.origin;
    return ALLOWED_RETURN_HOSTS.some((re) => re.test(origin));
  } catch {
    return false;
  }
}

export default function OAuthBridge() {
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get("return_to");
    const hash = window.location.hash || "";
    const sessionMatch = hash.match(/session_id=([^&]+)/);

    if (!returnTo || !sessionMatch) {
      // Nothing to forward — send the user to login on the current origin.
      window.location.replace("/login?error=oauth_bridge_missing_params");
      return;
    }

    if (!isAllowedReturnUrl(returnTo)) {
      window.location.replace("/login?error=oauth_bridge_disallowed_return");
      return;
    }

    const sep = returnTo.includes("#") ? "&" : "#";
    const target = `${returnTo}${sep}session_id=${encodeURIComponent(sessionMatch[1])}`;
    window.location.replace(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
      <Logo />
      <div className="font-mono text-sm text-muted-foreground">Redirecting…</div>
    </div>
  );
}
